if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');

      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    async waitForPropertySync() {
      this.form.dispatchEvent(new CustomEvent('product:before-add-to-cart', {
        bubbles: true,
        detail: { form: this.form }
      }));

      document.dispatchEvent(new CustomEvent('product:before-add-to-cart', {
        detail: { form: this.form }
      }));

      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    normalizeUploadcareUrl(url) {
      if (!url) return '';

      const trimmed = String(url).trim();

      // Already a proper Uploadcare file URL
      if (/^https:\/\/ucarecdn\.com\/[a-f0-9-]+\/?$/i.test(trimmed)) {
        return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
      }

      // Convert broken group URL like:
      // https://xxxxx.ucarecd.net/UUID~1/
      // to:
      // https://ucarecdn.com/UUID/
      const groupMatch = trimmed.match(/^https?:\/\/[^/]*ucarecd\.net\/([a-f0-9-]+)~\d+\/?$/i);
      if (groupMatch) {
        return `https://ucarecdn.com/${groupMatch[1]}/`;
      }

      // Convert raw UUID to proper URL if needed
      if (/^[a-f0-9-]+$/i.test(trimmed)) {
        return `https://ucarecdn.com/${trimmed}/`;
      }

      return trimmed;
    }

    normalizeUploadcarePropertyInputs() {
      const propertyInputs = this.form.querySelectorAll('[name^="properties["]');

      propertyInputs.forEach((input) => {
        const current = input.value || '';
        const normalized = this.normalizeUploadcareUrl(current);

        if (normalized && normalized !== current) {
          input.value = normalized;
          input.setAttribute('value', normalized);
        }
      });
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      // Wait for custom upload/property scripts to populate hidden inputs
      await this.waitForPropertySync();

      // Critical fix: normalize Uploadcare group URLs before building FormData
      this.normalizeUploadcarePropertyInputs();

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';

      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();

        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);
        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          window.location.href = theme.routes.cart;
          return;
        }

        ProductForm.updateCartIcon(data);

        const quickAddDrawer = this.closest('quick-add-drawer');
        if (quickAddDrawer) quickAddDrawer.addedToCart();

        if (this.cartDrawer) {
          this.cartDrawer.renderContents(
            data,
            !quickAddDrawer && theme.settings.afterAtc === 'drawer'
          );
        } else if (window.location.pathname === theme.routes.cart) {
          const cartItems = document.querySelector('cart-items');
          if (cartItems) {
            if (cartItems.dataset.empty === 'true') {
              window.location.reload();
              return;
            }
            await cartItems.refresh();
          }
        }

        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: {
            cart: data,
            variantId: data.variant_id
          }
        }));
      } catch (error) {
        console.log(error); // eslint-disable-line

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');

        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}